import React, { useState, useEffect } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import {
  Drawer,
  Typography,
  Box,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  InputAdornment,
  Link
} from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { navBarHeaderHeight } from '../lib/Constants'
import { useSelector } from 'react-redux'
import { getHelpCenterResources } from '../services/ApiService'
import { downloadWithSignedUrl, getSignedS3Url } from '../lib/Global'

const useStyles = makeStyles(theme => ({
  drawerPaper: {
    maxHeight: `calc(100% - calc(${navBarHeaderHeight + ' + 12px'}))`,
    marginTop: navBarHeaderHeight,
    width: '360px',
    borderRadius: '8px',
    overflow: 'auto',
    overflowX: 'hidden',
    zIndex: 1100,
    boxSizing: 'content-box',
    marginRight: '19px',
    marginBottom: '20px',
    display: 'flex',
    boxShadow: '2px 6px 19px 1px rgba(0,0,0,0.59)',
    [theme.breakpoints.down('xs')]: {
      marginRight: 'auto',
      width: '325px'
    }
  },
  header: {
    backgroundColor: theme.colors.invoiceColors.invoice_created,
    color: 'white',
    position: 'relative',
    height: '61px'
  },
  headerTitle: {
    fontSize: '25px',
    fontWeight: '700',
    textAlign: 'center',
    marginTop: '13px',
    color: theme.colors.white
  },
  drawerHeader: {
    display: 'flex',
    height: '60px',
    minHeight: '60px',
    alignItems: 'center',
    padding: '0 20px',
    borderBottom: `1px solid ${theme.colors.workOrders.downloadIcon}`
  },
  closeButton: {
    position: 'absolute',
    top: '12px',
    left: '10px',
    color: theme.colors.white,
    fontSize: '16px'
  },
  greeting: {
    padding: '20px',
    fontSize: '12px',
    fontWeight: '500',
    color: theme.colors.invoiceColors.labels_grey
  },
  userName: {
    fontWeight: '700'
  },
  content: {
    padding: '0 20px',
    flex: 1,
    display: 'flex',
    flexDirection: 'column'
  },
  accordion: {
    boxShadow: 'none',
    border: `1px solid ${theme.colors.textGray}`,
    borderRadius: '4px !important',
    marginBottom: '10px',
    backgroundColor: theme.colors.white,
    width: '100%',
    '&:before': {
      display: 'none'
    },
    '& .MuiAccordionSummary-root': {
      padding: '0px 15px'
    }
  },
  accordionSummary: {
    padding: '0 20px',
    minHeight: '48px !important',
    '& .MuiAccordionSummary-content': {
      margin: '12px 0 !important'
    }
  },
  accordionTitle: {
    fontSize: '12px',
    color: theme.colors.invoiceColors.dark_grey,
    fontWeight: 500
  },
  expandIcon: {
    color: theme.colors.invoiceColors.invoice_created,
    fontSize: '16px'
  },
  accordionDetails: {
    padding: '8px 15px'
  },
  inputContainer: {
    position: 'sticky',
    bottom: 0,
    padding: '20px',
    backgroundColor: theme.colors.white,
    // currently hidden as it will be used in a future development
    // for an interactive chat bot feature.
    display: 'none'
  },
  sendIcon: {
    color: theme.colors.textGray,
    cursor: 'pointer'
  },

  resourceName: {
    color: theme.colors.blue,
    fontSize: '12px',
    fontWeight: '500',
    lineHeight: 1,
    marginBottom: '2px',
    cursor: 'pointer'
  },
  resourceDesc: {
    color: theme.colors.grey,
    fontSize: '10px',
    lineHeight: 1

  },
  resourceItem: {
    '& + &': {
     marginTop: theme.spacing(3)
   }
  },
  noResourcesMessage: {
    color: theme.colors.grey,
    fontSize: '12px',
    fontStyle: 'italic'
  }

}))

export const HelpDrawer = ({ open, onClose }) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const [resources, setResources] = useState([])
  const userStore = useSelector(state => state.auth.user)
  const userName = userStore?.userInfo?.firstName || 'there'

  useEffect(() => {
    if (open) {
      getResources()
    }
  }, [open])

  const getResources = async () => {
    try {
      const response = await getHelpCenterResources()
      if (response?.success) {
        setResources(response.data)
      }
    } catch (error) {
      /* eslint-disable no-console */
      console.error('Error fetching resources:', error)
    }
  }

  const getFilteredResources = (type) => {
    return resources.filter(r => r.resourceType.toLowerCase() === type.toLowerCase())
  }

  const ResourceItem = ({ resource }) => {
    const handleResourceClick = async () => {
      if (resource.resourceType === 'document') {
        try {
          const fullPath = `subcontractor_documents/${resource.resourceUrl}`
          const downloadUrl = await getSignedS3Url(
            fullPath,
            '',
            'getObject'
          )
          if (downloadUrl) {
            await downloadWithSignedUrl(
              downloadUrl,
              userStore.userInfo?.parent_subdomain,
              resource.originalFileName
            )
          }
        } catch (error) {
          console.error('File could not be downloaded', error)
        }
      } else if (resource.resourceType === 'video') {
        window.open(resource.resourceUrl, '_blank', 'noopener,noreferrer')
      }
    }
    return (
      <Box className={classes.resourceItem}>
        <Link
          onClick={handleResourceClick}
        >
          <Typography className={classes.resourceName}>
            {resource.name}
          </Typography>
        </Link>
        <Typography className={classes.resourceDesc}>
          {resource.description}
        </Typography>
      </Box>
    )
  }

  const NoResourcesMessage = () => (
    <Typography className={classes.noResourcesMessage}>
      {t('general.help_center.no_resources_message')}
    </Typography>
  )

  return (
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        classes={{ paper: classes.drawerPaper }}
        BackdropProps={{ invisible: true }}
      >
        <Box className={classes.header}>
          <IconButton className={classes.closeButton} onClick={onClose}>
            <FontAwesomeIcon icon={['far', 'times-circle']} />
          </IconButton>
          <Typography className={classes.headerTitle}>
            {t('general.help_center.title')}
          </Typography>
        </Box>
        <Typography className={classes.greeting}>
          {t('general.help_center.greeting', { username: userName })}
        </Typography>

        <Box className={classes.content}>
          <Accordion className={classes.accordion}>
            <AccordionSummary
              expandIcon={<FontAwesomeIcon icon={['far', 'chevron-up']} className={classes.expandIcon} />}
              className={classes.accordionSummary}
            >
              <Typography className={classes.accordionTitle}>
                {t('general.help_center.videos_section')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.accordionDetails}>
              <Box width="100%">
                {getFilteredResources('video').length > 0 ? (
                  getFilteredResources('video').map(resource => (
                    <ResourceItem key={resource.id} resource={resource} />
                  ))
                ) : (
                  <NoResourcesMessage />
                )}
              </Box>
            </AccordionDetails>
          </Accordion>

          <Accordion className={classes.accordion}>
            <AccordionSummary
              expandIcon={<FontAwesomeIcon icon={['far', 'chevron-up']} className={classes.expandIcon} />}
              className={classes.accordionSummary}
            >
              <Typography className={classes.accordionTitle}>
                {t('general.help_center.documents_section')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.accordionDetails}>
              <Box width="100%">
                {getFilteredResources('document').length > 0 ? (
                  getFilteredResources('document').map(resource => (
                    <ResourceItem key={resource.id} resource={resource} />
                  ))
                ) : (
                  <NoResourcesMessage />
                )}
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>

        <Box className={classes.inputContainer}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Type or ask something"
            className={classes.input}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <FontAwesomeIcon
                    icon={['far', 'paper-plane']}
                    className={classes.sendIcon}
                  />
                </InputAdornment>
              )
            }}
          />
        </Box>
      </Drawer>
    )
}
